import React, { useState } from 'react';
import { CourtConfig } from '../../types';
import courtReserveLogo from '../../assets/integrations/courtreserve.svg';
import playByPointLogo from '../../assets/integrations/playbypoint.webp';
import googleCalendarLogo from '../../assets/integrations/google-calendar.png';

interface CourtCardProps {
  court: CourtConfig;
  isSelected: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

// Sport configurations with icons and gradients
const SPORT_CONFIG: Record<string, { icon: JSX.Element; gradient: string; label: string }> = {
  pickleball: {
    label: 'Pickleball',
    gradient: 'linear-gradient(135deg, #236332 0%, #3d9951 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
      </svg>
    ),
  },
  padel: {
    label: 'Padel',
    gradient: 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M7 7c3 1 7 1 10 0M7 17c3-1 7-1 10 0" />
      </svg>
    ),
  },
  basketball: {
    label: 'Basketball',
    gradient: 'linear-gradient(135deg, #dd7d47 0%, #f59e0b 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18M3 12c0-2.5 4-4.5 9-4.5s9 2 9 4.5M3 12c0 2.5 4 4.5 9 4.5s9-2 9-4.5" />
      </svg>
    ),
  },
  tennis: {
    label: 'Tennis',
    gradient: 'linear-gradient(135deg, #2f58dd 0%, #60a5fa 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M5.5 5.5c3.5 3.5 9.5 3.5 13 0M5.5 18.5c3.5-3.5 9.5-3.5 13 0" />
      </svg>
    ),
  },
  volleyball: {
    label: 'Volleyball',
    gradient: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3c0 3 2 5.5 5 7M12 3c0 3-2 5.5-5 7M12 21c0-5-4-8-9-9M12 21c0-5 4-8 9-9" />
      </svg>
    ),
  },
};

const COURT_TYPE_ICONS: Record<string, JSX.Element> = {
  indoor: (
    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
    </svg>
  ),
  outdoor: (
    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
    </svg>
  ),
  covered: (
    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
    </svg>
  ),
};

const INTEGRATION_META: Record<string, { name: string; logo: string }> = {
  courtreserve: { name: 'CourtReserve', logo: courtReserveLogo },
  playbypoint: { name: 'PlayByPoint', logo: playByPointLogo },
  'google-calendar': { name: 'Google Calendar', logo: googleCalendarLogo },
};

export default function CourtCard({
  court,
  isSelected,
  onClick,
  onEdit,
  onDelete,
}: CourtCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const sport = SPORT_CONFIG[court.sportType] || SPORT_CONFIG.pickleball;
  const courtTypeIcon = COURT_TYPE_ICONS[court.courtType] || COURT_TYPE_ICONS.indoor;
  // Extract a short identifier from the court name (trailing number or first 2 chars)
  const numMatch = court.name.match(/(\d+)\s*$/);
  const courtBadge = numMatch ? numMatch[1] : court.name.trim().slice(0, 2).toUpperCase();
  const courtTypeLabel = court.courtType.charAt(0).toUpperCase() + court.courtType.slice(1);
  const integrationMeta = court.syncedIntegrationType ? INTEGRATION_META[court.syncedIntegrationType] : null;
  const mappedExternalName = court.externalMappedName?.trim() || 'Not mapped';

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      className={`court-card-v2 ${isSelected ? 'court-card-v2--selected' : ''} ${!court.isActive ? 'court-card-v2--inactive' : ''}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="court-card-v2__selected-indicator">
          <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}

      {/* Court identifier badge with gradient background */}
      <div
        className="court-card-v2__icon"
        style={{ background: sport.gradient }}
        aria-label={`${sport.label} court ${courtBadge}`}
      >
        <span className="court-card-v2__icon-label">{courtBadge}</span>
      </div>

      {/* Content */}
      <div className="court-card-v2__content">
        <div className="court-card-v2__header">
          <h4 className="court-card-v2__name">{court.name}</h4>
          <div className={`court-card-v2__status ${court.isActive ? 'court-card-v2__status--active' : 'court-card-v2__status--inactive'}`}>
            <span className="court-card-v2__status-dot" />
            <span>{court.isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </div>

        <div className="court-card-v2__meta">
          <span className="court-card-v2__sport-badge" style={{
            background: `${sport.gradient.replace('135deg', '90deg')}`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            {sport.label}
          </span>
          <span className="court-card-v2__divider">•</span>
          <span className="court-card-v2__type">
            {courtTypeIcon}
            {courtTypeLabel}
          </span>
        </div>

        <div className="court-card-v2__mapped">
          <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12" aria-hidden="true">
            <path fillRule="evenodd" d="M5 8V6a5 5 0 1110 0v2h1a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2v-6a2 2 0 012-2h1zm2 0h6V6a3 3 0 10-6 0v2z" clipRule="evenodd" />
          </svg>
          <span className="court-card-v2__mapped-label">Mapped Name</span>
          <span className="court-card-v2__mapped-value" title={mappedExternalName}>{mappedExternalName}</span>
        </div>

        {integrationMeta && (
          <div className="court-card-v2__sync-chip" title={`Synced from ${integrationMeta.name}`}>
            <img src={integrationMeta.logo} alt={integrationMeta.name} />
            <span>Synced from {integrationMeta.name}</span>
          </div>
        )}

        {court.amenities && court.amenities.length > 0 && (
          <div className="court-card-v2__amenities">
            {court.amenities.slice(0, 3).map((amenity, i) => (
              <span key={i} className="court-card-v2__amenity-chip">{amenity}</span>
            ))}
            {court.amenities.length > 3 && (
              <span className="court-card-v2__amenity-more">+{court.amenities.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Actions - reveal on hover */}
      <div className={`court-card-v2__actions ${isHovered || isSelected ? 'court-card-v2__actions--visible' : ''}`}>
        <button
          className="court-card-v2__action-btn court-card-v2__action-btn--edit"
          onClick={handleEditClick}
          title="Edit court"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
        <button
          className="court-card-v2__action-btn court-card-v2__action-btn--delete"
          onClick={handleDeleteClick}
          title="Delete court"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}
